import { useState, useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { countryCodes } from '@/lib/constants/countries'
import {
  aggregateCities,
  aggregateCountries,
  aggregateRegions,
  getPeriodUniqueVisitorTotal,
  shareOf,
  type LocationCount,
} from '@/lib/calculations/breakdownTotals'
import { HugeiconsIcon } from "@hugeicons/react"
import { Search01Icon } from "@hugeicons/core-free-icons"
import {
  KpiStrip,
  RaisedCard,
  CardHead,
  Segmented,
  BarRowsHeader,
  BarRows,
  CardEmpty,
  Pagination,
  type BarRow,
} from '@/components/dashboard/analyticsTabs/TabPrimitives'
interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
  isLoading?: boolean
}

type TabType = 'countries' | 'cities' | 'regions'

interface LocationRow {
  name: string
  code?: string
  count: number
  percentage: number
}

/**
 * Locations section of the "Devices & locations" tab (was the Locations tab):
 * its own well under the devices well, below the page's single headline.
 * The page owns the loading state.
 */
/**
 * The breakdown endpoint returns at most 50 rows per dimension and no distinct
 * count, so a full list means "at least 50", never exactly 50.
 */
const BREAKDOWN_ROW_CAP = 50
const capped = (n: number) => (n >= BREAKDOWN_ROW_CAP ? `${BREAKDOWN_ROW_CAP}+` : n)

export default function LocationsSection() {
  const ctx = useOutletContext<DashboardContext>()
  const dashboardData = ctx.dashboardData
  const isLoading = ctx.isLoading

  const [currentTab, setCurrentTab] = useState<TabType>('countries')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 15

  // Process location data from dashboardData. `metrics.aggregate` mixes every
  // breakdown dimension and city/region rows carry their parent country, so each
  // tab reads only its own dimension's rows (see lib/calculations/breakdownTotals).
  // Shares are taken against the period's deduped unique-visitor total, not a row
  // sum: rows only cover visitors whose location was detected and the API returns
  // the top entries per dimension.
  const { countryData, cityData, regionData } = useMemo(() => {
    const periodTotal = getPeriodUniqueVisitorTotal(dashboardData?.metrics)
    if (!dashboardData?.metrics?.aggregate) {
      return { countryData: [], cityData: [], regionData: [], totalSessions: periodTotal }
    }

    const aggregate = dashboardData.metrics.aggregate
    const countryLabel = (code: string) => countryCodes[code] || code

    const toRows = (items: LocationCount[], label: (item: LocationCount) => string): LocationRow[] => {
      const denominator = periodTotal || items.reduce((sum, item) => sum + item.count, 0)
      return items.map((item) => ({
        name: label(item),
        code: item.country || undefined,
        count: item.count,
        percentage: shareOf(item.count, denominator),
      }))
    }

    return {
      countryData: toRows(aggregateCountries(aggregate), (item) => countryLabel(item.country)),
      cityData: toRows(aggregateCities(aggregate), (item) => `${item.name}, ${countryLabel(item.country)}`),
      regionData: toRows(aggregateRegions(aggregate), (item) => `${item.name}, ${countryLabel(item.country)}`),
      totalSessions: periodTotal,
    }
  }, [dashboardData])

  // Get current data based on tab
  const currentData = useMemo(() => {
    switch (currentTab) {
      case 'countries': return countryData
      case 'cities': return cityData
      case 'regions': return regionData
      default: return countryData
    }
  }, [currentTab, countryData, cityData, regionData])

  // Filter and paginate
  const filteredData = useMemo(() => {
    if (!searchTerm) return currentData
    return currentData.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [currentData, searchTerm])

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  // Reset page when changing tabs or search
  useEffect(() => {
    setCurrentPage(1)
  }, [currentTab, searchTerm])

  if (isLoading) return null

  const topCountry = countryData[0]
  const tabNoun = currentTab === 'countries' ? 'Country' : currentTab === 'cities' ? 'City' : 'Region'
  const rows: BarRow[] = paginatedData.map((item, index) => ({
    key: `${item.name}-${index}`,
    label: item.name,
    value: formatNumeric(item.count),
    share: item.percentage,
    leading: item.code ? (
      <span className="w-7 shrink-0 rounded-[4px] bg-background py-px text-center text-xs font-medium uppercase text-muted-foreground ring-1 ring-border">
        {item.code}
      </span>
    ) : undefined,
  }))

  return (
    <div className="well flex flex-col gap-2">
      <KpiStrip
        tiles={[
          { label: 'Countries', value: capped(countryData.length) },
          { label: 'Cities', value: capped(cityData.length) },
          { label: 'Regions', value: capped(regionData.length) },
          { label: 'Top country', value: topCountry?.name || '--' },
        ]}
      />

      <RaisedCard>
        <CardHead title="Locations">
          <Segmented
            value={currentTab}
            onChange={setCurrentTab}
            options={[
              { value: 'countries', label: 'Countries' },
              { value: 'cities', label: 'Cities' },
              { value: 'regions', label: 'Regions' },
            ]}
          />
          <div className="relative w-full sm:w-56">
            <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={`Search ${currentTab}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </CardHead>
        <BarRowsHeader label={tabNoun} valueLabel="Unique visitors" />
        {rows.length > 0 ? (
          <BarRows rows={rows} />
        ) : (
          <CardEmpty>
            {searchTerm ? `No ${currentTab} match your search` : `No ${currentTab} data available`}
          </CardEmpty>
        )}
        {totalPages > 1 ? (
          <Pagination
            from={(currentPage - 1) * itemsPerPage + 1}
            to={Math.min(currentPage * itemsPerPage, filteredData.length)}
            total={filteredData.length}
            onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            prevDisabled={currentPage === 1}
            nextDisabled={currentPage === totalPages}
          />
        ) : (
          <div className="h-2" />
        )}
      </RaisedCard>
    </div>
  )
}
